import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'

interface CountUpProps {
    value: number
    duration?: number
    className?: string
}

// Counts from zero to `value` the first time it scrolls into view.
export const CountUp: FC<CountUpProps> = ({ value, duration = 1600, className }): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)
    const [display, setDisplay] = useState(0)

    useEffect(() => {
        const node = ref.current
        if (!node) return

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setDisplay(value)
            return
        }

        let frame = 0

        const observer = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return

            observer.disconnect()
            const start = performance.now()

            const tick = (now: number): void => {
                const progress = Math.min((now - start) / duration, 1)
                const eased = 1 - Math.pow(1 - progress, 3)

                setDisplay(Math.round(eased * value))
                if (progress < 1) frame = requestAnimationFrame(tick)
            }

            frame = requestAnimationFrame(tick)
        }, { threshold: 0.4 })

        observer.observe(node)

        return () => {
            observer.disconnect()
            cancelAnimationFrame(frame)
        }
    }, [value, duration])

    return (
        <span ref={ref} className={className}>
            {display.toLocaleString('en-US')}
        </span>
    )
}
